import { isRouteActive } from '../hooks/useLocalNavigationEntry';
import { type LocalNavigationLink, type LocalNavigationMenuItem } from '../hooks/useLocalNavigationMenuSections';

type LocalNavigationSubmenuItemType = Extract<LocalNavigationMenuItem, { type: 'submenu' }>;

export const getAccordionKey = (sectionTitle: string, itemId: number): string => `${sectionTitle}-${itemId}`;

export const hasDirectUrl = (item: LocalNavigationMenuItem): boolean => !!item.url;

export const getNavigableLinks = (item: LocalNavigationSubmenuItemType): LocalNavigationLink[] =>
  item.links.filter((link) => !!link.url);

export const getChildLinks = (item: LocalNavigationSubmenuItemType): LocalNavigationLink[] =>
  item.links.filter((link) => !!link.url || !!link.action);

export const hasChildren = (item: LocalNavigationSubmenuItemType): boolean => getChildLinks(item).length > 0;

export const getCollapsedEntry = (
  item: LocalNavigationMenuItem
): { url: string; targetEnvironment?: string; icon?: LocalNavigationLink['icon']; text: string } | null => {
  if (item.type === 'link' && item.url) {
    return { url: item.url, targetEnvironment: item.targetEnvironment, icon: item.icon, text: item.text };
  }
  if (item.type === 'submenu' && item.url) {
    return { url: item.url, targetEnvironment: item.targetEnvironment, icon: item.icon, text: item.text };
  }
  if (item.type !== 'submenu') return null;
  const firstLink = getNavigableLinks(item)[0];
  if (!firstLink?.url) return null;

  return {
    url: firstLink.url,
    targetEnvironment: firstLink.targetEnvironment || item.targetEnvironment,
    icon: item.icon,
    text: item.text,
  };
};

export const getDirectEntry = (
  item: LocalNavigationMenuItem
): (Extract<LocalNavigationMenuItem, { type: 'link' }> | LocalNavigationLink) | null => {
  if (item.type === 'link') return item;
  if (hasDirectUrl(item)) return item;
  const firstLink = getNavigableLinks(item)[0];
  if (!firstLink || hasChildren(item)) return null;

  return { ...firstLink, icon: firstLink.icon ?? item.icon ?? undefined, text: firstLink.text || item.text };
};

export const isItemActive = (pathname: string, item: LocalNavigationMenuItem): boolean => {
  if (item.type === 'link') return isRouteActive(pathname, item);
  if (hasDirectUrl(item) && isRouteActive(pathname, item)) return true;

  return getNavigableLinks(item).some((link) =>
    isRouteActive(pathname, { url: link.url, targetEnvironment: link.targetEnvironment || item.targetEnvironment })
  );
};
